import React, { useState, useEffect, useContext, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Minus,
  RotateCcw,
  Star,
  ShieldAlert,
} from "lucide-react";
import organizationsService from "../../services/organizationsService";
import AuthContext from "../../context/AuthContext";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";
import RecognitionLogsCard from "./RecognitionLogsCard";

const SettingsSection = () => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.is_superuser;

  const [selectedOrgId, setSelectedOrgId] = useState(null);

  // Points settings state
  const [pointsSettings, setPointsSettings] = useState({ min: 10, max: 100, step: 10, dollarRate: 1.0 });
  const [showPointsResetConfirm, setShowPointsResetConfirm] = useState(false);

  // Content moderation (profanity filter) state
  const [moderation, setModeration] = useState({ enabled: true, customWords: "" });

  // Fetch organizations for the dropdown
  const { data: organizations = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationsService.getAllOrganizations(),
  });

  // Set org: use user's org for org admins, first org for super admins
  useEffect(() => {
    if (!selectedOrgId) {
      if (!isSuperAdmin && user?.organization_id) {
        setSelectedOrgId(user.organization_id);
      } else if (organizations.length > 0) {
        setSelectedOrgId(organizations[0].id);
      }
    }
  }, [organizations, selectedOrgId, isSuperAdmin, user?.organization_id]);

  // Load points settings when organization changes. Seed only once per org:
  // background refetches of ["organizations"] must not wipe unsaved edits.
  const seededOrgRef = useRef(null);
  useEffect(() => {
    if (selectedOrgId && organizations.length > 0 && seededOrgRef.current !== selectedOrgId) {
      const org = organizations.find((o) => o.id === selectedOrgId);
      if (org) {
        seededOrgRef.current = selectedOrgId;
        console.log("[SettingsSection] Loading org settings:", {
          orgId: org.id,
          orgName: org.name,
          min_recognition_points: org.min_recognition_points,
          max_recognition_points: org.max_recognition_points,
          points_step: org.points_step,
        });
        setPointsSettings({
          min: org.min_recognition_points ?? 10,
          max: org.max_recognition_points ?? 100,
          step: org.points_step ?? 10,
          dollarRate: org.points_to_dollar_rate ?? 1.0,
          allowGlobalPosting: org.allow_global_point_posting ?? false,
        });
        setModeration({
          enabled: org.profanity_filter_enabled ?? true,
          customWords: org.profanity_custom_words || "",
        });
      }
    }
  }, [selectedOrgId, organizations]);

  // Update points settings mutation
  const updatePointsMutation = useMutation({
    mutationFn: (settings) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error("No organization selected"));
      }
      const payload = {
        min_recognition_points: settings.min,
        max_recognition_points: settings.max,
        points_step: settings.step,
        points_to_dollar_rate: settings.dollarRate,
        allow_global_point_posting: settings.allowGlobalPosting,
      };
      console.log("[SettingsSection] Saving points settings:", payload, "for org:", selectedOrgId);
      return organizationsService.updateOrganization(selectedOrgId, payload);
    },
    onSuccess: (response) => {
      console.log("[SettingsSection] Save successful, response:", response);
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Points settings saved successfully!");
    },
    onError: (error) => {
      console.error("[SettingsSection] Save failed:", error);
      toast.error(formatError(error));
    },
  });

  // Save content-moderation (profanity filter) settings
  const updateModerationMutation = useMutation({
    mutationFn: (settings) => {
      if (!selectedOrgId) {
        return Promise.reject(new Error("No organization selected"));
      }
      return organizationsService.updateOrganization(selectedOrgId, {
        profanity_filter_enabled: settings.enabled,
        profanity_custom_words: settings.customWords.trim() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Moderation settings saved!");
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Handle points settings change
  const handlePointsChange = (field, value) => {
    let newValue;
    if (field === 'dollarRate') {
      // Dollar rate: min 0.01, max 100, allow decimals
      newValue = Math.max(0.01, Math.min(100, parseFloat(value) || 0.01));
      // Round to 2 decimal places
      newValue = Math.round(newValue * 100) / 100;
    } else {
      const maxLimit = field === 'step' ? 100 : 1000;
      // Minimum points may be 0 (recognitions without points); the rest start at 1.
      const minLimit = field === 'min' ? 0 : 1;
      newValue = Math.max(minLimit, Math.min(maxLimit, value));
    }
    const newSettings = { ...pointsSettings, [field]: newValue };

    // Ensure min <= max
    if (field === 'min' && newValue > pointsSettings.max) {
      newSettings.max = newValue;
    }
    if (field === 'max' && newValue < pointsSettings.min) {
      newSettings.min = newValue;
    }

    setPointsSettings(newSettings);
  };

  // Save points settings
  const savePointsSettings = () => {
    if (pointsSettings.min > pointsSettings.max) {
      toast.error("Minimum points cannot be greater than maximum");
      return;
    }
    updatePointsMutation.mutate(pointsSettings);
  };

  // Reset points to defaults (preserves the global-posting setting)
  const resetPointsToDefaults = () => {
    const reset = {
      min: 10,
      max: 100,
      step: 10,
      dollarRate: 1.0,
      allowGlobalPosting: pointsSettings.allowGlobalPosting ?? false,
    };
    setPointsSettings(reset);
    updatePointsMutation.mutate(reset);
    setShowPointsResetConfirm(false);
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg sm:text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1">
          Configure points settings and review recognition activity
        </p>
      </div>

      {/* Points Settings Card */}
      <div className="bg-white rounded-2xl shadow-md p-3 sm:p-6">
        <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2 flex-wrap">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2 min-w-0">
            <Star className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
            <span className="truncate">Points Settings</span>
          </h2>
          {isSuperAdmin && (
            <select
              value={selectedOrgId || ""}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs sm:text-sm max-w-[160px] sm:max-w-none flex-shrink-0"
              disabled={isLoadingOrgs}
            >
              {isLoadingOrgs ? (
                <option>Loading...</option>
              ) : organizations.length === 0 ? (
                <option>No organizations</option>
              ) : (
                organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))
              )}
            </select>
          )}
        </div>
        <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6">
          Configure the minimum and maximum points that users can award when sending recognitions.
        </p>

        <div className="space-y-6">
          {/* Min Points */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Minimum Points
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handlePointsChange('min', pointsSettings.min - 10)}
                disabled={pointsSettings.min <= 0}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <input
                type="number"
                min="0"
                max="1000"
                value={pointsSettings.min}
                onChange={(e) => handlePointsChange('min', parseInt(e.target.value, 10) || 0)}
                className="w-24 px-3 py-2 text-center border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => handlePointsChange('min', pointsSettings.min + 10)}
                disabled={pointsSettings.min >= pointsSettings.max}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <span className="text-sm text-slate-500">points</span>
            </div>
          </div>

          {/* Max Points */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Maximum Points
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handlePointsChange('max', pointsSettings.max - 10)}
                disabled={pointsSettings.max <= pointsSettings.min}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <input
                type="number"
                min="1"
                max="1000"
                value={pointsSettings.max}
                onChange={(e) => handlePointsChange('max', parseInt(e.target.value) || 1)}
                className="w-24 px-3 py-2 text-center border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => handlePointsChange('max', pointsSettings.max + 10)}
                disabled={pointsSettings.max >= 1000}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <span className="text-sm text-slate-500">points</span>
            </div>
          </div>

          {/* Step/Increments */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Increment Step
            </label>
            <p className="text-xs text-slate-500 mb-2">
              How much the points change when users click +/- buttons
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handlePointsChange('step', pointsSettings.step - 5)}
                disabled={pointsSettings.step <= 1}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <input
                type="number"
                min="1"
                max="100"
                value={pointsSettings.step}
                onChange={(e) => handlePointsChange('step', parseInt(e.target.value) || 1)}
                className="w-24 px-3 py-2 text-center border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => handlePointsChange('step', pointsSettings.step + 5)}
                disabled={pointsSettings.step >= 100}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <span className="text-sm text-slate-500">points per click</span>
            </div>
          </div>

          {/* Points to Dollar Rate */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Points to Dollar Conversion Rate
            </label>
            <p className="text-xs text-slate-500 mb-2">
              How many dollars each point is worth in the marketplace
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handlePointsChange('dollarRate', Math.max(0.01, (pointsSettings.dollarRate || 1) - 0.1))}
                disabled={(pointsSettings.dollarRate || 1) <= 0.1}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <input
                  type="number"
                  min="0.01"
                  max="100"
                  step="0.01"
                  value={pointsSettings.dollarRate ?? 1.0}
                  onChange={(e) => handlePointsChange('dollarRate', parseFloat(e.target.value) || 0.01)}
                  className="w-28 pl-7 pr-3 py-2 text-center border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <button
                type="button"
                onClick={() => handlePointsChange('dollarRate', Math.min(100, (pointsSettings.dollarRate || 1) + 0.1))}
                disabled={(pointsSettings.dollarRate || 1) >= 100}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <span className="text-sm text-slate-500">per point</span>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Example: 100 points = ${((pointsSettings.dollarRate ?? 1.0) * 100).toFixed(2)} in the marketplace
            </p>
          </div>

          {/* Public point amounts */}
          <div>
            <label className="flex items-start gap-2 sm:gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!pointsSettings.allowGlobalPosting}
                onChange={(e) =>
                  setPointsSettings({ ...pointsSettings, allowGlobalPosting: e.target.checked })
                }
                className="h-4 w-4 mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 flex-shrink-0"
              />
              <span className="text-xs sm:text-sm font-medium text-slate-700">
                Allow recognition point amounts to be posted globally
              </span>
            </label>
            <p className="text-[11px] sm:text-xs text-slate-500 mt-1.5 sm:mt-2 ml-6 sm:ml-7">
              When enabled, senders can choose to make a recognition's point amount public. When disabled, point amounts are always hidden from everyone except the sender and admins.
            </p>
          </div>

          {/* Preview — plain inline text so the pills flow with the surrounding words instead of each wrapping to its own line. */}
          <div className="p-3 sm:p-4 bg-slate-50 rounded-2xl">
            <div className="text-xs sm:text-sm text-slate-600 mb-1.5 sm:mb-2">Preview:</div>
            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
              Users can award between{" "}
              <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium align-middle">
                {pointsSettings.min}
              </span>{" "}
              and{" "}
              <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium align-middle">
                {pointsSettings.max}
              </span>{" "}
              points, in increments of{" "}
              <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium align-middle">
                {pointsSettings.step}
              </span>.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-slate-200">
            <button
              onClick={savePointsSettings}
              disabled={updatePointsMutation.isPending}
              className="px-3 sm:px-4 py-1.5 sm:py-2 org-bg-primary org-btn text-white rounded-full text-xs sm:text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {updatePointsMutation.isPending ? "Saving..." : "Save Changes"}
            </button>
            <button
              onClick={() => setShowPointsResetConfirm(true)}
              disabled={updatePointsMutation.isPending}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-slate-600 hover:text-orange-600 hover:bg-orange-50 rounded-full transition-colors border border-slate-200 whitespace-nowrap"
            >
              <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Reset to Defaults
            </button>
          </div>
        </div>
      </div>

      {/* Content Moderation Card */}
      <div className="bg-white rounded-2xl shadow-md p-3 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1">
          <ShieldAlert className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
          <span className="truncate">Content Moderation</span>
        </h2>
        <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6">
          Block comments that contain profanity or other NSFW words from being posted.
        </p>

        <div className="space-y-5">
          {/* Enable toggle */}
          <div>
            <label className="flex items-start gap-2 sm:gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!moderation.enabled}
                onChange={(e) =>
                  setModeration({ ...moderation, enabled: e.target.checked })
                }
                className="h-4 w-4 mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 flex-shrink-0"
              />
              <span className="text-xs sm:text-sm font-medium text-slate-700">
                Block profanity in comments
              </span>
            </label>
            <p className="text-[11px] sm:text-xs text-slate-500 mt-1.5 sm:mt-2 ml-6 sm:ml-7">
              When enabled, a comment containing any of these words can’t be posted — the sender is asked to edit it first.
            </p>
          </div>

          {/* Custom words */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Additional blocked words
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Comma-separated. Added to the built-in NSFW list. Matched whole-word and case-insensitive.
            </p>
            <textarea
              value={moderation.customWords}
              onChange={(e) =>
                setModeration({ ...moderation, customWords: e.target.value })
              }
              disabled={!moderation.enabled}
              rows={3}
              placeholder="e.g. wordone, wordtwo, wordthree"
              className="w-full px-3 py-2 border border-slate-300 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>

          {/* Save */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-slate-200">
            <button
              onClick={() => updateModerationMutation.mutate(moderation)}
              disabled={updateModerationMutation.isPending}
              className="px-3 sm:px-4 py-1.5 sm:py-2 org-bg-primary org-btn text-white rounded-full text-xs sm:text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {updateModerationMutation.isPending ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {/* Recognition Logs Card */}
      <RecognitionLogsCard />

      {/* Points Reset Confirmation Modal */}
      {showPointsResetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-orange-100 rounded-full">
                <RotateCcw className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Reset Points to Defaults?</h3>
            </div>
            <p className="text-slate-600 mb-6">
              This will reset the points range to the default values (10 - 100 points).
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPointsResetConfirm(false)}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={resetPointsToDefaults}
                disabled={updatePointsMutation.isPending}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {updatePointsMutation.isPending ? "Resetting..." : "Reset"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsSection;
